import { useEffect, useState } from "react";
import { multiFacetCountsFromAlgolia } from "../../../../../../shared/api/postCall/multiFacetCountsFromAlgolia";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";

/**
 * usePayerPatternOptions
 *
 * Custom hook that fetches payer pattern options (Insurance Plans) from Algolia.
 * Handles loading state and error handling.
 *
 * @param {string} facilityId - The facility ID to fetch payer patterns for
 * @param {boolean} isOpen - Whether the modal/dialog is open (controls when to fetch)
 * @returns {object} Return object containing:
 * @returns {string[]} return.payerPatternOptions - Array of payer pattern options (sorted)
 * @returns {boolean} return.isLoading - Loading state for the fetch operation
 */
export const usePayerPatternOptions = (facilityId: string, isOpen: boolean) => {
  const [payerPatternOptions, setPayerPatternOptions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && facilityId) {
      setIsLoading(true);
      const orgId = getOrgIdForFetchExternalWorklist() || facilityId;

      multiFacetCountsFromAlgolia({
        org_id: orgId,
        facet_attributes: ["primary_payer_name"], // Only fetch Insurance Plans
        timezone_offset_minutes: new Date().getTimezoneOffset(),
      })
        .then((response) => {
          // Extract Insurance Plans from facet_results
          const insurancePlans = Object.keys(
            response?.facet_results?.primary_payer_name || {},
          );
          setPayerPatternOptions(insurancePlans.sort());
        })
        .catch((error) => {
          console.error("Error fetching Insurance Plans from Algolia:", error);
          setPayerPatternOptions([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, facilityId]);

  return {
    payerPatternOptions,
    isLoading,
  };
};
